import { SKILL_NAMES } from 'js/src/agent/skills/skillNames';
import type { CodeoSkill } from '../types';
import { check, countOf, someTween } from '../skillUsage';

export const lowerThirdCalloutSkill: CodeoSkill = {
    name: SKILL_NAMES.lowerThirdCallout,
    kind: 'codeo',
    description:
        'Name a person, place, or source with a lower-third callout that rises in over the bottom ' +
        'of the frame, holds while it is needed, and clears itself — for attributions, speaker ' +
        'identifications, locations, and product names.',
    documentUsesSkill: check(
        (script, markup) =>
            /bottom:\s*\d/.test(markup) &&
            someTween(script, /opacity:\s*1\b/) &&
            countOf(script, /opacity:\s*0\b/g) >= 2,
    ),
    instructions: `# Lower-Third Callout

A small identifying block sits in the lower-left of the frame: a name on one line, a role or qualifier under it, an accent rule between them. It rises in, holds long enough to be read, and then leaves — the leaving is what separates a lower third from a caption that overstays.

The technique is three staggered entrances (name, rule, role) and a mirrored exit. The rule drawing left-to-right between the two lines is what makes it read as broadcast rather than as a floating label.

## Markup

Position from the *bottom* edge, not the top: the block should sit at a constant distance from the frame's lower edge regardless of how tall the name wraps. Give the text a shadow so it survives whatever it overlays.

\`\`\`html
<div id="callout" style="position: absolute; left: 130px; bottom: 130px; display: flex; flex-direction: column; align-items: flex-start; gap: 14px;">
  <div id="callout-name" style="font-family: var(--l5-font-0), sans-serif; font-size: 76px; font-weight: 700; line-height: 0.96; color: var(--l5-color-0, #ffffff); text-shadow: 0 2px 18px rgba(0,0,0,0.45);">Dr. Maya Chen</div>
  <div id="callout-rule" style="width: 320px; height: 6px; background: var(--l5-color-1, #38bdf8); transform-origin: left center;"></div>
  <div id="callout-role" style="font-family: var(--l5-font-0), sans-serif; font-size: 34px; font-weight: 500; letter-spacing: 0.04em; color: var(--l5-color-0, #ffffff); text-shadow: 0 2px 14px rgba(0,0,0,0.5);">Head of Research</div>
</div>
\`\`\`

Label the name and the role separately — they are the two things a user will ask to change.

## Timeline code

\`\`\`js
const tl = gsap.timeline({ paused: true });
const HOLD_END = 4.2;

gsap.set('#callout-name', { y: 28, opacity: 0 });
gsap.set('#callout-rule', { scaleX: 0 });
gsap.set('#callout-role', { y: 16, opacity: 0 });

tl.to('#callout-name', { y: 0, opacity: 1, duration: 0.55, ease: 'power3.out' }, 0.1);
tl.to('#callout-rule', { scaleX: 1, duration: 0.5, ease: 'power4.out' }, 0.3);
tl.to('#callout-role', { y: 0, opacity: 1, duration: 0.5, ease: 'power3.out' }, 0.46);

tl.to('#callout-role', { opacity: 0, duration: 0.3, ease: 'power2.in' }, HOLD_END);
tl.to('#callout-rule', { scaleX: 0, duration: 0.3, ease: 'power2.in' }, HOLD_END + 0.05);
tl.to('#callout-name', { y: -16, opacity: 0, duration: 0.32, ease: 'power2.in' }, HOLD_END + 0.1);

window.__timelines = { main: tl };
\`\`\`

The exit runs in reverse order of the entrance — role first, name last — so the block collapses toward the thing that mattered instead of dissolving all at once.

## Adapt

The rail above is the quiet default. The same three-part structure carries every common lower-third look; change the container, not the choreography:

- **Solid bar**: put the two lines inside a filled \`background: var(--l5-color-1)\` block with generous padding, and wipe the block in with \`clipPath: 'inset(0 100% 0 0)'\` → \`'inset(0 0% 0 0)'\` instead of sliding the text.
- **Side rule**: drop the horizontal rule and run a vertical one down the left edge (\`scaleY\` from a \`top center\` origin), with the text indented past it.
- **Kicker**: swap the role line above the name and set it smaller and letter-spaced — reads as a category rather than a description.
- **Dark card**: a translucent rounded panel behind both lines (\`background: rgba(0,0,0,0.55); backdrop-filter: blur(8px)\`) for footage too busy for text shadow alone.
- **Stacked bars**: give each line its own background block and stagger their clip-path wipes by 0.12s.

Timing: \`HOLD_END\` should leave the block on screen for at least 2.5s of settled hold. If the scene has narration, enter on the speaker's name and exit once the sentence naming them is done.

## Pitfalls

- Anchor with \`bottom\`, not \`top\` — a name that wraps to two lines grows upward from a bottom anchor and downward off the frame from a top one.
- The rule is \`scaleX\`-tweened, so it needs \`transform-origin: left center\` and must not carry a CSS \`transform\` of its own — GSAP replaces the CSS contribution and the rule jumps.
- \`gsap.set\` establishes every from-state at build time here, so no tween needs \`immediateRender: false\`.
- Give it an exit. A lower third that never leaves stops being an identification and becomes furniture — and a still block sitting in frame for the whole scene is exactly the dead air the tail of a graphic dies from.
- One per scene. Two lower thirds at once means neither is the answer to "who is this".
- Don't restate the narration: if the voiceover says the name, the callout carries the role, not both.
- Keep the role line short enough to sit on one line — a wrapping role is a sentence, and this is a label.`,
};
